/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Table
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";

// Utils
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";

// Date picker
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from 'papaparse';

function TransactionReport() {
  const { transactionReportcolumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const api = useApi();

  // ✅ REQUIRED state
  const [transactions, setTransactions] = useState([]);

  // ✅ Same helper as SettlementReport
  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  };

  // ✅ Store Date objects (NOT strings)
  const [dateRange, setDateRange] = useState([getTodayDateAtMidnight(), getTodayDateAtMidnight()]);

  // ✅ Same formatter as SettlementReport
  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return "";
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  // ✅ API call (NO state mutation here)
  const getTransactionSummary = (range) => {
    if (!range || range.length !== 2) return;

    const payload = {
      fromDate: formatDate(range[0]),
      toDate: formatDate(range[1]),
    };

    api
      .post("/adminPortal/reports/qr-transactions-mdr", payload)
      .then((response) => {
        const { responseCode, data: rows, message } = response.data || {};
        // Success but no data
        if (responseCode === 200 && (!rows || rows.length === 0)) {
          setTransactions([]);
          return;
        }
        // Success with data
        if (responseCode === 200) {
          setTransactions(rows);
          return;
        }
        // Business-level failure
        toast.error(message || "Failed to fetch transaction report", {
          duration: 1000,
        });
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to view transaction reports.");
          return;
        }
        toast.error("Something went wrong. Please try again later.", {
          duration: 1000,
        });
      });
  };
  const generateCSV = () => {
      if (transactions.length === 0) {
        return '';
      }
  
      const headers = transactionReportcolumns.map(column => column.Header);
  
      const summaryWithUppercaseColumns = transactions.map(item => {
        return transactionReportcolumns.map(column => {
          const accessor = column.accessor;
          const value = typeof accessor === 'function' ? accessor(item) : item[accessor];
          return value;
        });
      });
  
      const csvData = [headers, ...summaryWithUppercaseColumns];
  
      const csv = Papa.unparse(csvData, {
        quotes: true,
      });
  
      return csv;
    };
  const handleDownloadCSV = () => {
    if (transactions.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false });
      setTimeout(() => toast.dismiss(toastId), 1000);
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `TransactionReport_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  // ✅ API trigger (single source of truth)
  useEffect(() => {
    getTransactionSummary(dateRange);
  }, [dateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <MDBox mb={3} position="relative" display="flex" justifyContent="center">
              <DateRangePicker
                disabledDate={afterToday()}
                ranges={predefinedRanges}
                onChange={(range) => {
                  if (range && range.length === 2) {
                    setDateRange(range);
                  }
                }}
                format="dd MMM yyyy"
                value={dateRange}
              />
              <MDButton
                variant="gradient"
                onClick={handleDownloadCSV}
                color="info"
                style={{ position: "absolute", right: 0, top: 0 }}
              >
                Export
              </MDButton>
            </MDBox>
            <DataTable
              table={{ columns: transactionReportcolumns, rows: transactions }}
              showTotalEntries
              isSorted
              noEndBorder
              entriesPerPage={{ defaultValue: 25 }}
              entriesPerPagePosition="bottom-right"
              canSearch
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default TransactionReport;
